// Pages/UserAccount/UserAccount.jsx

import React from 'react';
import { useParams } from 'react-router-dom';
import UserAccountGridLayout from './Layout/UserAccountGridLayout';

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------
function MapPinIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function UsersIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function ImageIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  );
}

function FilmIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
      <line x1="7" y1="2" x2="7" y2="22" />
      <line x1="17" y1="2" x2="17" y2="22" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="2" y1="7" x2="7" y2="7" />
      <line x1="2" y1="17" x2="7" y2="17" />
      <line x1="17" y1="17" x2="22" y2="17" />
      <line x1="17" y1="7" x2="22" y2="7" />
    </svg>
  );
}

function VideoIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  );
}

function UserPlusIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  );
}

function UserMinusIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  );
}

function BanIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  );
}

function AlertTriangleIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function MoreHorizIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}

function ArrowLeftIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function determineFetchMode(suserid, tuserid) {
  const hasS = Boolean(suserid && suserid.trim() !== '');
  const hasT = Boolean(tuserid && tuserid.trim() !== '');
  if (hasS && hasT) return 'suserid-tuserid';
  if (!hasS && hasT) return 'tuserid-only';
  if (hasS && !hasT) return 'suserid-only';
  return 'unknown';
}

function buildUserData(fetchMode, suserid, tuserid) {
  return { suserid, tuserid };
}

// ---------------------------------------------------------------------------
// Tab config
// ---------------------------------------------------------------------------
const TABS = [
  { id: 'images', label: 'Images', Icon: ImageIcon },
  { id: 'reels',  label: 'Reels',  Icon: FilmIcon  },
  { id: 'videos', label: 'Videos', Icon: VideoIcon  },
];

// ---------------------------------------------------------------------------
// MoreDropdown (local component)
// ---------------------------------------------------------------------------
function MoreDropdown({ onAction }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  // close on outside click
  React.useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const fire = (actionId) => {
    onAction(actionId);
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="ua-icon-btn"
        aria-label="More options"
        onClick={() => setOpen((v) => !v)}
      >
        <MoreHorizIcon />
      </button>

      {open && (
        <div className="ua-dropdown" role="menu">
          <button className="ua-dropdown__item" onClick={() => fire('add-friend')}>
            <UserPlusIcon />
            <span>Add as Friend</span>
          </button>
          <button className="ua-dropdown__item" onClick={() => fire('remove-friend')}>
            <UserMinusIcon />
            <span>Remove as Friend</span>
          </button>

          <div className="ua-dropdown__divider" />

          <button className="ua-dropdown__item ua-dropdown__item--danger" onClick={() => fire('block')}>
            <BanIcon />
            <span>Block</span>
          </button>
          <button className="ua-dropdown__item ua-dropdown__item--danger" onClick={() => fire('report-abuse')}>
            <AlertTriangleIcon />
            <span>Report Abuse</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProfileAvatar placeholder
// ---------------------------------------------------------------------------
function ProfileAvatar({ src, userId }) {
  const [imgError, setImgError] = React.useState(false);
  const initials = userId
    ? userId.slice(0, 2).toUpperCase()
    : '??';

  if (src && !imgError) {
    return (
      <img
        className="ua-avatar"
        src={src}
        alt={userId || 'avatar'}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div className="ua-avatar ua-avatar--placeholder">
      <span>{initials}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UserAccount page
// ---------------------------------------------------------------------------
function UserAccount() {
  const { suserid, tuserid } = useParams();

  const [activeTab, setActiveTab]         = React.useState('images');
  const [formattedData, setFormattedData] = React.useState(null);
  const [pageVisible, setPageVisible]     = React.useState(false);

  // Simple CSS-based entry animation trigger
  React.useEffect(() => {
    const raf = requestAnimationFrame(() => setPageVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const fetchMode = determineFetchMode(suserid, tuserid);
  const userData  = buildUserData(fetchMode, suserid, tuserid);

  // ---- More action handler ----
  function handleMoreAction(actionId) {
    console.log(`[UserAccount] More action: "${actionId}"`, {
      suserid,
      tuserid,
      /*
       * Future REST hooks:
       *   'add-friend'    → POST /api/friends/add      { suserid, tuserid }
       *   'remove-friend' → DELETE /api/friends/remove { suserid, tuserid }
       *   'block'         → POST /api/users/block      { suserid, tuserid }
       *   'report-abuse'  → POST /api/reports          { reportedBy: suserid, reportedUser: tuserid }
       */
    });
  }

  const profileImage = formattedData?.profileImage  ?? '';
  const displayId    = formattedData?.userId        ?? tuserid ?? '';
  const location     = formattedData?.location      ?? '';
  const nfriends     = formattedData?.nfriends       ?? 0;

  const isBadMode = fetchMode === 'suserid-only' || fetchMode === 'unknown';

  return (
    <div className={`ua-page${pageVisible ? ' ua-page--visible' : ''}`} style={{background: "rgba(36,36,45,0.2)"}}>

      {/* ── Row 1: Go Back ── */}
      <div className="ua-row ua-row--back">
        <button
          className="ua-back-btn"
          onClick={() => window.history.back()}
          aria-label="Go back"
        >
          <ArrowLeftIcon />
          <span>Go Back</span>
        </button>
      </div>

      {/* ── Row 2: Profile bar ── */}
      <div className="ua-row ua-row--topbar" style={{marginTop: 30}}>
        {/* LEFT: avatar + identity */}
        <div className="ua-profile-identity">
          <ProfileAvatar src={profileImage} userId={displayId} />
          <div className="ua-profile-text">
            <span className="ua-profile-userid">
              {displayId || <span className="ua-placeholder-text">Loading…</span>}
            </span>
            {(location || !formattedData) && (
              <span className="ua-profile-location">
                <MapPinIcon size={12} />
                <span>{location || '—'}</span>
              </span>
            )}
          </div>
        </div>

        {/* RIGHT: friends pill + tabs + more */}
        <div className="ua-topbar-right">
          {/* Friends pill */}
          <div className="ua-friends-pill">
            <UsersIcon size={13} />
            <span>{nfriends} Friends</span>
          </div>

          {/* Segmented tab control */}
          <div className="ua-tabs" role="tablist">
            {TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                role="tab"
                aria-selected={activeTab === id}
                className={`ua-tab${activeTab === id ? ' ua-tab--active' : ''}`}
                onClick={() => setActiveTab(id)}
              >
                <Icon />
                <span>{label}</span>
              </button>
            ))}
          </div>

          {/* More dropdown */}
          <MoreDropdown onAction={handleMoreAction} />
        </div>
      </div>

      {/* ── Divider ── */}
      <div className="ua-divider" />

      {/* ── Error: bad fetch mode ── */}
      {isBadMode && (
        <div className="ua-mode-error">
          <p>
            Cannot determine which user to load. Expected URL format:
            <code> /user-account/&#123;suserid&#125;/&#123;tuserid&#125;</code>
          </p>
          <p>
            Received: suserid=<strong>{suserid || '(empty)'}</strong>,
            tuserid=<strong>{tuserid || '(empty)'}</strong>
          </p>
        </div>
      )}

      {/* ── Grid ── */}
      {!isBadMode && (
        <main className="ua-main">
          <UserAccountGridLayout
            fetchMode={fetchMode}
            userData={userData}
            contentType={activeTab}
            onDataLoaded={setFormattedData}
          />
        </main>
      )}

      {/* ── Footer ── */}
      <div className="uaf-footer" style={{marginBottom: 15 }}>
        <p className="uaf-footer-text" style={{fontWeight: "normal", color: "white", marginTop: -20}}>
          Kleido AI, an Intelligent social platform. Copyright © InitiumNet Technology Pvt Ltd.
        </p>
      </div>

      {/* ── Scoped styles ── */}
      <style>{`
        /* ─── page shell ─── */
        .ua-page {
          min-height: 100vh;
          background: "black";
          color: #e2e8f0;
          display: flex;
          flex-direction: column;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          opacity: 0;
          transform: translateY(12px);
          transition: opacity 0.35s ease, transform 0.35s ease;
        }
        .ua-page--visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* ─── rows ─── */
        .ua-row {
          width: 100%;
          box-sizing: border-box;
          padding: 10px 20px;
        }
        .ua-row--back {
          padding-top: 14px;
          padding-bottom: 6px;
        }
        .ua-row--topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          padding-top: 8px;
          padding-bottom: 10px;
        }

        /* ─── back button ─── */
        .ua-back-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: transparent;
          border: 1px solid gray;
          color: #9ca3af;
          padding: 5px 13px 5px 10px;
          border-radius: 6px;
          font-size: 13px;
          cursor: pointer;
          transition: background 0.15s, color 0.15s, border-color 0.15s;
        }
        .ua-back-btn:hover {
          background: "rgba(36, 36, 45, 0.2)";
          color: #e2e8f0;
          border-color: gray;
        }

        /* ─── profile identity (left) ─── */
        .ua-profile-identity {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }
        .ua-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid gray;
          flex-shrink: 0;
        }
        .ua-avatar--placeholder {
          background: #1e2035;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 600;
          color: white;
        }
        .ua-profile-text {
          display: flex;
          flex-direction: column;
          gap: 3px;
          min-width: 0;
        }
        .ua-profile-userid {
          font-size: 15px;
          font-weight: 600;
          color: #f1f5f9;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ua-profile-location {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #6b7280;
        }
        .ua-placeholder-text {
          color: #4b5563;
          font-style: italic;
        }

        /* ─── right section ─── */
        .ua-topbar-right {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        /* friends pill */
        .ua-friends-pill {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          background: #1a1d2e;
          border: 1px solid #2a2d3e;
          border-radius: 20px;
          padding: 4px 12px;
          font-size: 12.5px;
          color: #9ca3af;
          white-space: nowrap;
        }

        /* segmented tabs */
        .ua-tabs {
          display: flex;
          background: #12152a;
          border: 1px solid #2a2d3e;
          border-radius: 8px;
          overflow: hidden;
        }
        .ua-tab {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 6px 14px;
          font-size: 12.5px;
          font-weight: 500;
          background: transparent;
          border: none;
          color: #6b7280;
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
          white-space: nowrap;
        }
        .ua-tab + .ua-tab {
          border-left: 1px solid #2a2d3e;
        }
        .ua-tab:hover {
          background: #1a1d2e;
          color: #d1d5db;
        }
        .ua-tab--active {
          background: "rgba(36, 36, 45, 0.2)";
          color: #fff;
        }
        .ua-tab--active:hover {
          background: "darkslategray";
          color: #fff;
        }

        /* icon button */
        .ua-icon-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          background: #1a1d2e;
          border: 1px solid #2a2d3e;
          border-radius: 8px;
          color: #9ca3af;
          cursor: pointer;
          transition: background 0.15s, color 0.15s, border-color 0.15s;
        }
        .ua-icon-btn:hover {
          background: #22263a;
          color: #e2e8f0;
          border-color: "gray";
        }

        /* ─── dropdown ─── */
        .ua-dropdown {
          position: absolute;
          top: calc(100% + 6px);
          right: 0;
          min-width: 190px;
          background: #1a1d2e;
          border: 1px solid #2a2d3e;
          border-radius: 10px;
          padding: 6px 0;
          box-shadow: 0 8px 24px rgba(0,0,0,0.45);
          z-index: 200;
          animation: ua-dropdown-in 0.15s ease;
        }
        @keyframes ua-dropdown-in {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .ua-dropdown__item {
          display: flex;
          align-items: center;
          gap: 9px;
          width: 100%;
          padding: 9px 16px;
          background: transparent;
          border: none;
          color: #c9d1d9;
          font-size: 13.5px;
          cursor: pointer;
          text-align: left;
          transition: background 0.12s, color 0.12s;
        }
        .ua-dropdown__item:hover {
          background: #22263a;
          color: #f1f5f9;
        }
        .ua-dropdown__item--danger {
          color: #f87171;
        }
        .ua-dropdown__item--danger:hover {
          background: rgba(239,68,68,0.12);
          color: #fca5a5;
        }
        .ua-dropdown__divider {
          height: 1px;
          background: #2a2d3e;
          margin: 5px 0;
        }

        /* ─── divider ─── */
        .ua-divider {
          width: 100%;
          height: 1px;
          background: #1e2235;
        }

        /* ─── main content ─── */
        .ua-main {
          flex: 1;
          width: 100%;
          box-sizing: border-box;
        }

        /* ─── mode error ─── */
        .ua-mode-error {
          margin: 32px auto;
          max-width: 540px;
          background: rgba(239,68,68,0.08);
          border: 1px solid rgba(239,68,68,0.3);
          border-radius: 10px;
          padding: 18px 22px;
          color: #fca5a5;
          font-size: 13.5px;
          line-height: 1.6;
        }
        .ua-mode-error p { margin: 0 0 6px; }
        .ua-mode-error p:last-child { margin-bottom: 0; }
        .ua-mode-error code {
          background: rgba(255,255,255,0.08);
          padding: 1px 5px;
          border-radius: 4px;
          font-size: 12.5px;
        }

        /* ─── footer ─── */
        .ua-footer {
          padding: 16px 20px;
          text-align: center;
          font-size: 12px;
          color: #374151;
          border-top: 1px solid #1e2235;
        }
      `}</style>
    </div>
  );
}

export default UserAccount;
